import React from 'react';
import PromptContainer, { PromptContainerProps } from './PromptContainer';
import Stack, { contentStackSpacing } from '../Stack';
import Text from '../Text';
import { useAssertHasNestedScrollViewPortal } from '../NestedScrollViewPortal';
import { IconName } from '../Icon';
import Button, { RenderButtonContentArgs } from '../Button';
import { mainContentPadding } from '../MainContentContainer';
import { PRScrollView } from '../../shared/Scroll';
import { Platform, View } from 'react-native';

export type SelectListPromptOption = {
    label: string;
    value: string;
    icon?: IconName;
    testID?: string;
    leftContent?: React.ReactNode;
};

export type SingleValueSelectListProps = {
    multiSelect?: false;
    value: string | null;
    onChange: (value: string | null) => void;
};

export type MultiValueSelectListProps = {
    multiSelect: true;
    value: string[];
    onChange: (value: string[]) => void;
};

export type SelectListPromptValueProps = SingleValueSelectListProps | MultiValueSelectListProps;

export type SelectListPromptOwnProps = {
    title: string;
    options: SelectListPromptOption[];
    /**
     * If provided, this function will be called when the option is selected.
     *
     * Note that this does not replace onChange, but is an additional callback.
     */
    onSelect?: (value: string) => void;
    renderSelectButtonContent?: (args: RenderButtonContentArgs) => React.ReactNode;
} & SelectListPromptValueProps &
    Omit<PromptContainerProps, 'renderPromptContents' | 'mobileMaxHeight' | 'desktopMaxHeight'>;

export type SelectListPromptProps = SelectListPromptOwnProps;

const SelectListPrompt: React.FC<SelectListPromptProps> = ({
    title,
    options,
    onChange,
    value,
    multiSelect,
    onSelect,
    renderSelectButtonContent,
    ...toPass
}) => {
    useAssertHasNestedScrollViewPortal();

    const getIsSelected = (option: SelectListPromptOption) => {
        if (multiSelect) {
            return value.includes(option.value);
        }
        return value === option.value;
    };

    const handleSelect = (option: SelectListPromptOption) => {
        onSelect?.(option.value);
        if (multiSelect) {
            if (value.includes(option.value)) {
                onChange(value.filter((v) => v !== option.value));
            } else {
                onChange([...value, option.value]);
            }
        } else {
            onChange(option.value === value ? null : option.value);
        }
    };

    return (
        <PromptContainer
            {...toPass}
            mobileMaxHeight={'80%'}
            renderPromptContents={(onClose) => (
                <View
                    style={{
                        padding: mainContentPadding,
                        paddingBottom: 0,
                        alignSelf: 'stretch',
                        flex: Platform.OS === 'web' ? 1 : undefined,
                        flexGrow: 1,
                        flexShrink: 1,
                    }}
                    testID="select-options-container"
                >
                    <Text variant="h4" style={{ marginBottom: 4, flexShrink: 0, height: 24 }}>
                        {title}
                    </Text>
                    <PRScrollView
                        style={{ width: '100%', flexShrink: 1, flexGrow: 1 }}
                        contentContainerStyle={{
                            flexGrow: 1,
                            paddingBottom: mainContentPadding,
                        }}
                        showsVerticalScrollIndicator
                    >
                        <Stack vertical fullWidth space={contentStackSpacing}>
                            {options.map((option) => (
                                <Button
                                    width={'100%'}
                                    key={option.value}
                                    variant="select"
                                    size="lg"
                                    title={option.label}
                                    leftIcon={option.icon}
                                    icon={getIsSelected(option) ? 'Check' : undefined}
                                    onPress={(e) => {
                                        e.preventDefault();
                                        handleSelect(option);
                                        if (!multiSelect) {
                                            onClose();
                                        }
                                    }}
                                    justifyButtonContent="flex-start"
                                    iconColor="Success"
                                    leftContent={option.leftContent}
                                    testID={`select-option-${option.testID || option.value}`}
                                    renderButtonContent={renderSelectButtonContent}
                                />
                            ))}
                        </Stack>
                    </PRScrollView>
                </View>
            )}
        />
    );
};

export default React.memo(SelectListPrompt);
